import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Loader, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './NeedToSignNdaModal.styles.scss';

type GetRoute = Promise<
  AxiosResponse<{
    url: string;
  }>
>;

export type NeedToSignNdaModalRef = Omit<ModalRef, 'open'> & {
  open: (getRoute: GetRoute) => void;
};

type NeedToSignNdaModalProps = {
  className?: string;
} & ModalProps;

const NeedToSignNdaModal: React.ForwardRefRenderFunction<
  NeedToSignNdaModalRef,
  NeedToSignNdaModalProps
> = (props, ref) => {
  const { children, className, ...rest } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [url, setUrl] = useState('');

  const { popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const open = useCallback(
    async (getRoute: GetRoute) => {
      try {
        modal.open();
        setUrl('');
        setIsLoading(true);
        const { data } = await getRoute;
        if (data?.url) setUrl(data.url);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [modal, popServerError],
  );

  useImperativeHandle(ref, () => ({ ...modal, open }), [modal, open]);

  const classes = classNames('need-to-sign-nda-modal', className);

  const { t } = useTranslation();

  return (
    <Modal ref={modalRef} className={classes} {...rest}>
      {children}

      <div className="actions">
        <Button variant="link" onClick={modal?.close}>
          {t('close')}
        </Button>

        <Button
          type="submit"
          disabled={isLoading || !url}
          onClick={() => {
            setIsLoading(true);
            window.location.href = url;
          }}
        >
          {isLoading ? <Loader size="small" /> : t('signNda')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(NeedToSignNdaModal);
